/**
 * Service worker registration (audit P-12, 2026-10-22).
 *
 * The worker is registered ONLY in production builds, and only when the browser
 * supports it. In development there is no worker, so the usual hot-reload and
 * network behaviour are untouched. Registration is a progressive enhancement:
 * any failure is logged and the app keeps working online.
 */

/** @returns {boolean} true when a worker should be registered in this build/runtime. */
export function shouldRegister(env, nav) {
  return env === 'production' && !!nav && 'serviceWorker' in nav;
}

/**
 * Registers the service worker when appropriate. Safe to call unconditionally.
 * @param {object} [opts]
 * @param {string} [opts.env] - NODE_ENV (defaults to process.env.NODE_ENV).
 * @param {string} [opts.publicUrl] - PUBLIC_URL (defaults to process.env.PUBLIC_URL).
 */
export function register(opts = {}) {
  const env = opts.env ?? process.env.NODE_ENV;
  const publicUrl = opts.publicUrl ?? process.env.PUBLIC_URL ?? '';

  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  if (!shouldRegister(env, navigator)) return;

  // The worker must be same-origin as the page or the browser rejects it.
  try {
    const resolved = new URL(publicUrl || '/', window.location.href);
    if (resolved.origin !== window.location.origin) return;
  } catch {
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${publicUrl}/service-worker.js`;
    navigator.serviceWorker.register(swUrl).catch((err) => {
      // Non-fatal: the app runs online without the worker.
      console.warn('Service worker registration failed:', err);
    });
  });
}

/** Unregisters any existing worker (used to disable the PWA shell). */
export function unregister() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then((reg) => reg.unregister())
    .catch((err) => console.warn('Service worker unregister failed:', err));
}
